// Copyright (c) Ping. All rights reserved.
//
// `GuidedTourHost` wraps one product tour: it themes `@reactour/tour` with
// the app tokens, gates the tour on the per-user "seen" flag, serialises it
// against other first-run guides through the onboarding coordinator, and
// runs each step's side effect (switch tab / settings pane) as the user
// advances. Mount one host per tour; it renders nothing until its turn.

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { type StepType, TourProvider, useTour } from '@reactour/tour';

import { tokens, useColorScheme } from '@ping/ui';

import { GuidedTourCard } from './GuidedTourCard';
import { GuidedTourContext } from './GuidedTourContext';
import type { GuidedStep } from './GuidedTourTypes';
import { useOnboardingCoordinator } from './onboardingCoordinator';
import { dismissTour } from './tourDismiss';
import type { TourId } from './tourIds';
import { useTourReplay } from './tourReplayStore';
import { markTourSeen, readTourSeen } from './tourTracking';

interface Palette {
  readonly surface: string;
  readonly border: string;
  readonly textPrimary: string;
  readonly maskOverlay: string;
  readonly radius: number;
}

function paletteFor(scheme: 'light' | 'dark'): Palette {
  const c = tokens.color.semantic[scheme];
  return {
    // Match the shared AnchoredPopover / tools-menu surface so the tour card
    // reads as one of our popovers (surface panel: #F5F6F8 light, #15171C dark).
    surface: c.surface.panel,
    border: c.border.subtle,
    textPrimary: c.text.primary,
    radius: tokens.radius.lg,
    // A touch lighter than the recovery coachmark so the dimmed shell (and
    // the pane changing behind a settings step) stays readable.
    maskOverlay: scheme === 'dark' ? 'rgba(0,0,0,0.55)' : 'rgba(15,23,42,0.4)',
  };
}

// The tour is the top-most layer, always. It is portaled to <body> (see
// GuidedTourHost) so these values compete at the root stacking context and
// beat every other app layer: the Settings modal (a react-native-web Modal
// portal at z-index 9999) and the shared AnchoredPopover (2147483640) that
// renders the "All tools" menu. The popover sits at the 32-bit max, its mask
// one below.
const TOUR_MASK_Z_INDEX = 2_147_483_646;
const TOUR_POPOVER_Z_INDEX = 2_147_483_647;

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (): void => setReduced(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}

function toReactourStep(step: GuidedStep): StepType {
  const observe = [...(step.observe ?? [step.selector])];
  return {
    selector: step.selector,
    // Consumed by our `GuidedTourCard` via context, not by Reactour; kept
    // non-empty only to satisfy the `content` field's type.
    content: step.title,
    // Watch the target so the popover re-anchors once `onEnter` navigates
    // and the element mounts into the (previously hidden) pane.
    mutationObservables: observe,
    resizeObservables: observe,
    // We drive navigation ourselves; the user interacts via the popover.
    stepInteraction: false,
    ...(step.position !== undefined ? { position: step.position } : {}),
    ...(step.padding !== undefined ? { padding: { mask: step.padding } } : {}),
  };
}

export interface GuidedTourHostProps {
  readonly tourId: TourId;
  readonly steps: readonly GuidedStep[];
  /** Higher wins when several guides wait together. */
  readonly priority: number;
  /**
   * Extra precondition beyond the seen flag (e.g. the settings modal must be
   * open). When it goes false mid-tour the tour is dismissed. Defaults true.
   */
  readonly enabled?: boolean;
  /**
   * Manual, on-demand tour (e.g. the per-pane guide): never auto-starts and
   * never marks itself seen, so it only runs when explicitly launched via the
   * replay nonce and can be re-opened any number of times. Defaults false.
   */
  readonly manual?: boolean;
}

export function GuidedTourHost({
  tourId,
  steps,
  priority,
  enabled = true,
  manual = false,
}: GuidedTourHostProps): JSX.Element {
  const palette = paletteFor(useColorScheme() === 'dark' ? 'dark' : 'light');
  const reducedMotion = usePrefersReducedMotion();
  const reactourSteps = useMemo<StepType[]>(() => steps.map(toReactourStep), [steps]);
  const contextValue = useMemo(
    () => ({ tourId, steps, markSeenOnDismiss: !manual }),
    [tourId, steps, manual],
  );

  // Reactour's TourProvider snapshots `steps` into internal state on mount only
  // (`useState(defaultSteps)`) and ignores later prop changes. Tours whose steps
  // change at runtime (the per-pane guide swaps its whole step list when you
  // switch panes) must therefore REMOUNT the provider so it adopts the new
  // steps; keying it by the step identities does exactly that. Stable-step tours
  // (home / settings) keep a constant key and never remount.
  const stepsKey = useMemo(() => steps.map((s) => s.id).join('|'), [steps]);

  const content = (
    <GuidedTourContext.Provider value={contextValue}>
      <TourProvider
        key={stepsKey}
        steps={reactourSteps}
        components={{ Content: GuidedTourCard }}
        showNavigation={false}
        showBadge={false}
        showDots={false}
        showCloseButton={false}
        disableInteraction
        scrollSmooth={!reducedMotion}
        padding={{ mask: 6, popover: 12 }}
        onClickMask={({ setIsOpen }) => dismissTour(tourId, setIsOpen, !manual)}
        onClickClose={({ setIsOpen }) => dismissTour(tourId, setIsOpen, !manual)}
        styles={{
          popover: (base) => ({
            ...base,
            background: palette.surface,
            color: palette.textPrimary,
            borderRadius: palette.radius,
            border: `1px solid ${palette.border}`,
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.24)',
            padding: 16,
            maxWidth: 360,
            zIndex: TOUR_POPOVER_Z_INDEX,
          }),
          maskArea: (base) => ({ ...base, rx: 12 }),
          maskWrapper: (base) => ({
            ...base,
            color: palette.maskOverlay,
            zIndex: TOUR_MASK_Z_INDEX,
          }),
        }}
      >
        <GuidedTourRunner
          tourId={tourId}
          steps={steps}
          priority={priority}
          enabled={enabled}
          manual={manual}
        />
      </TourProvider>
    </GuidedTourContext.Provider>
  );

  // Reactour renders its mask and popover INLINE (no portal of its own), so
  // left in the shell tree they are trapped in whatever stacking context the
  // shell creates and can be covered by the Settings modal (a body-level RN
  // Modal at z-9999) or the "All tools" AnchoredPopover (body-level at
  // 2147483640) no matter how high our z-index is. Portaling the whole tour to
  // <body> lifts it to the top-level stacking context, where our z-index above
  // wins. React context (router, color scheme) is preserved across the portal.
  if (typeof document === 'undefined') {
    return content;
  }
  return createPortal(content, document.body);
}

type Gate = 'loading' | 'show' | 'skip';

function GuidedTourRunner({
  tourId,
  steps,
  priority,
  enabled,
  manual,
}: Required<GuidedTourHostProps>): null {
  const { isOpen, setIsOpen, setCurrentStep, currentStep } = useTour();
  const active = useOnboardingCoordinator((s) => s.active);
  const request = useOnboardingCoordinator((s) => s.request);
  const release = useOnboardingCoordinator((s) => s.release);

  const replayNonce = useTourReplay((s) => s.nonces[tourId] ?? 0);

  const [gate, setGate] = useState<Gate>('loading');
  // Lifecycle latches, kept as refs so they never re-trigger effects:
  //   requested  — we have asked the coordinator for a slot.
  //   openTriggered — we have called setIsOpen(true) for this grant.
  //   openConfirmed — Reactour actually opened (isOpen went true).
  const requestedRef = useRef(false);
  const openTriggeredRef = useRef(false);
  const openConfirmedRef = useRef(false);
  const replayNonceRef = useRef(replayNonce);

  // Read the per-user seen flag once the precondition is met. Manual tours
  // never auto-start: they stay idle until launched via the replay nonce.
  useEffect(() => {
    if (manual || !enabled) {
      setGate('skip');
      return;
    }
    let cancelled = false;
    void (async () => {
      const seen = await readTourSeen(tourId);
      if (!cancelled) {
        setGate(seen ? 'skip' : 'show');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [manual, enabled, tourId]);

  // Manual replay (e.g. from Help & About): force a fresh run regardless of
  // the seen flag, as long as the precondition still holds.
  useEffect(() => {
    if (replayNonce === replayNonceRef.current) {
      return;
    }
    replayNonceRef.current = replayNonce;
    if (replayNonce === 0 || !enabled) {
      return;
    }
    requestedRef.current = false;
    openTriggeredRef.current = false;
    openConfirmedRef.current = false;
    setGate('show');
  }, [replayNonce, enabled]);

  // Ask the coordinator for the screen when this tour should run.
  useEffect(() => {
    if (gate === 'show' && !requestedRef.current) {
      requestedRef.current = true;
      request(tourId, priority);
    }
  }, [gate, tourId, priority, request]);

  // Defensive: if this runner unmounts while holding (or queued for) the slot
  // (e.g. the provider is remounted because the step list changed), free it so
  // the next tour can run.
  useEffect(() => {
    return () => {
      if (requestedRef.current || openConfirmedRef.current) {
        release(tourId);
      }
    };
  }, [tourId, release]);

  // Open once granted (guarded so we only trigger the open a single time).
  useEffect(() => {
    if (gate === 'show' && active === tourId && !openTriggeredRef.current) {
      openTriggeredRef.current = true;
      setCurrentStep(0);
      setIsOpen(true);
    }
  }, [gate, active, tourId, setCurrentStep, setIsOpen]);

  // Run the current step's side effect (switch tab / settings pane).
  useEffect(() => {
    if (!isOpen || active !== tourId) {
      return;
    }
    const step = steps[currentStep];
    if (step?.onEnter !== undefined) {
      void step.onEnter();
    }
  }, [isOpen, active, tourId, currentStep, steps]);

  // Confirm the open, then detect a close from any source (mask, Skip, Done).
  // Using a "was actually open" latch avoids a same-commit race where the
  // teardown would fire on the very render that requests the open.
  useEffect(() => {
    if (isOpen) {
      openConfirmedRef.current = true;
      return;
    }
    if (openConfirmedRef.current) {
      openConfirmedRef.current = false;
      openTriggeredRef.current = false;
      requestedRef.current = false;
      setGate('skip');
      // Idempotent: the dismissal path already released; this backstops a
      // close that bypassed it and stops us reopening.
      release(tourId);
    }
  }, [isOpen, tourId, release]);

  // The precondition dropped (e.g. the settings modal closed): stand down.
  // Mark it seen only if the tour was actually shown; a still-queued tour
  // just frees its slot so it can run next time the precondition returns.
  useEffect(() => {
    if (enabled || !requestedRef.current) {
      return;
    }
    const wasShown = openConfirmedRef.current;
    requestedRef.current = false;
    openTriggeredRef.current = false;
    openConfirmedRef.current = false;
    setGate('skip');
    if (wasShown && !manual) {
      void markTourSeen(tourId);
    }
    release(tourId);
    setIsOpen(false);
  }, [enabled, manual, tourId, release, setIsOpen]);

  return null;
}
